import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ClipboardList, ShieldAlert, Truck, HardHat, Users, FolderTree, ChevronRight, AlertTriangle } from "lucide-react";
import { MasciLogo } from "@/components/MasciLogo";
import DemoSubmissionBanner from "@/components/DemoSubmissionBanner";
import RoleWelcomeStrip from "@/components/RoleWelcomeStrip";
import TrainingCenterCTA from "@/components/TrainingCenterCTA";
import OperationalActivityTicker from "@/components/OperationalActivityTicker";
import RolePainReliefStrip from "@/components/RolePainReliefStrip";
import OperationsCommandView from "@/components/OperationsCommandView";
import EquipmentStatusBoard from "@/components/EquipmentStatusBoard";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/lib/useTenant";
import { clearAdminToken } from "@/lib/adminAuth";
import { clearPmToken } from "@/lib/pmAuth";
import { clearShopToken } from "@/lib/shopAuth";

/**
 * SuperintendentHub — `/super`
 *
 * Focused field-leadership surface for the Superintendent role. NOT an
 * admin portal preview. Shows the live OperationsCommandView (jobsite
 * heartbeat) plus a tight stack of field-leadership quick-links —
 * Daily Reports · Crew · Equipment · Incidents · Inspections · Meetings.
 *
 * Deliberately omits admin-only data-management surfaces (Job Master,
 * Employee Master, Equipment Master uploads, system recovery, dev portal)
 * — those are not the Superintendent's job and would dilute the role feel.
 */

const FIELD_LEADERSHIP_ACTIONS = [
  {
    key: "daily",
    icon: ClipboardList,
    label: "Daily Reports",
    sub: "Today's submissions · by job · by crew",
    href: "/admin/daily",
    accent: "#FF6A00",
  },
  {
    key: "equipment",
    icon: Truck,
    label: "Equipment Status",
    sub: "OOS · Pre-Op fails · readiness",
    href: "/admin/equipment",
    accent: "#FF6A6A",
  },
  {
    key: "incidents",
    icon: ShieldAlert,
    label: "Incidents · Near-Misses",
    sub: "Open investigations · 24h alerts",
    href: "/admin/incidents",
    accent: "#FFD000",
  },
  {
    key: "meetings",
    icon: HardHat,
    label: "Toolbox Talks",
    sub: "Today's attendance · 7d roll-up",
    href: "/admin/meetings",
    accent: "#65DA76",
  },
  {
    key: "inspections",
    icon: ClipboardList,
    label: "Inspections · QA/QC",
    sub: "Field walks · concrete pours · MOT",
    href: "/admin/qaqc",
    accent: "#FF6A00",
  },
  {
    key: "jobs",
    icon: FolderTree,
    label: "Active Jobs",
    sub: "Open scope · MOT · staging",
    href: "/admin",
    accent: "#FF6A00",
  },
];

export default function SuperintendentHub() {
  const tenant = useTenant();
  const navigate = useNavigate();

  const signOut = () => {
    try {
      clearAdminToken(); clearPmToken(); clearShopToken();
      window.localStorage.removeItem("forgeops.demo.identity");
    } catch { /* */ }
    navigate("/demo");
  };

  return (
    <div className="min-h-screen" style={{ background: "#0A0A0A" }} data-testid="superintendent-hub">
      {/* Header — minimal, mission-critical */}
      <header
        className="border-b sticky top-0 z-30"
        style={{ background: "#070707", borderColor: "#1A1A1A" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Link to="/demo" className="inline-flex items-center gap-2" data-testid="super-home-link">
            <MasciLogo className="h-7 sm:h-8 w-auto" />
            <span
              className="hidden sm:inline-flex items-center px-2 py-0.5 rounded font-mono text-[9px] uppercase tracking-[0.25em]"
              style={{ background: "rgba(255,106,0,0.14)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.45)" }}
            >
              Superintendent · Field Command
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/guide"
              className="inline-flex items-center h-10 px-3 rounded font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ background: "transparent", color: "#BDBDBD", border: "1px solid #2A2A2A" }}
              data-testid="super-guide-link"
            >
              Guide
            </Link>
            <Button
              onClick={signOut}
              variant="outline"
              data-testid="super-signout-btn"
              className="h-10 border-2 border-slate-700 bg-transparent text-slate-300 hover:border-red-500 hover:text-red-400 text-xs font-bold uppercase tracking-wide"
            >
              <LogOut className="w-3.5 h-3.5 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8 sm:py-10">
        {/* Demo persistence loop — visitor's last /demo Toolbox Talk surfaces here */}
        <DemoSubmissionBanner portal="admin" />

        {/* Role-aware welcome strip — for super@ this renders the
            FIELD LEADERSHIP · OPERATIONAL PRESSURE variant with manpower /
            equipment OOS / weather impact metrics. */}
        <RoleWelcomeStrip />

        <OperationalActivityTicker />

        <RolePainReliefStrip role="super" />

        <TrainingCenterCTA />

        {/* Live jobsite status — the heartbeat. SAME OperationsCommandView
            as /admin but here it's the FOCUS, not a sidebar. */}
        <OperationsCommandView />

        {/* Field-leadership action stack — what a super opens at 5:45am */}
        <section className="mt-10" data-testid="super-actions">
          <div className="mb-4 flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#FF6A00" }}>
                FIELD LEADERSHIP · QUICK ACTIONS
              </div>
              <h2 className="font-display text-xl sm:text-2xl font-black tracking-tight text-white leading-tight">
                Run the work. Open what you need.
              </h2>
            </div>
            <span className="font-mono text-[9px] uppercase tracking-[0.25em]" style={{ color: "#8A8A8A" }}>
              tap to open
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px rounded-xl overflow-hidden border" style={{ background: "#1A1A1A", borderColor: "#1F1F1F" }}>
            {FIELD_LEADERSHIP_ACTIONS.map((a) => (
              <ActionTile key={a.key} action={a} />
            ))}
          </div>
        </section>

        {/* Equipment status board — second-most-checked surface for a super */}
        <section className="mt-10">
          <div className="mb-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#FF6A00" }}>
              EQUIPMENT · LIVE STATUS BOARD
            </div>
            <h2 className="font-display text-xl sm:text-2xl font-black tracking-tight text-white leading-tight">
              Every unit. Every job. Right now.
            </h2>
          </div>
          <EquipmentStatusBoard />
        </section>

        {/* Field-leadership signature — sets the tone */}
        <section className="mt-12 text-center">
          <p
            className="font-mono text-[10px] uppercase tracking-[0.3em]"
            style={{ color: "#8A8A8A" }}
          >
            {(tenant.brand_name || "FORGEDOPS").toUpperCase()} · field leadership surface · built by people who run the work
          </p>
        </section>
      </main>
    </div>
  );
}

function ActionTile({ action }) {
  const Icon = action.icon;
  return (
    <Link
      to={action.href}
      data-testid={`super-${action.key}`}
      className="group block p-5 transition active:scale-[0.99]"
      style={{ background: "#0F0F0F", minHeight: 96 }}
    >
      <div className="flex items-center gap-4">
        <span
          className="inline-flex items-center justify-center w-12 h-12 rounded-md shrink-0"
          style={{
            background: `${action.accent}1A`,
            color: action.accent,
            border: `1px solid ${action.accent}55`,
          }}
        >
          <Icon className="w-6 h-6" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base sm:text-lg font-black tracking-tight text-white leading-tight">
            {action.label}
          </div>
          <div className="text-xs sm:text-sm mt-0.5" style={{ color: "#BDBDBD" }}>
            {action.sub}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 shrink-0 transition-transform group-hover:translate-x-0.5" style={{ color: "#FF6A00" }} />
      </div>
    </Link>
  );
}
